import React, { useState, useRef, useEffect } from 'react';
import Slider from 'react-slick';
import Image from './Image';
import Video from './Video';
import s from './ImageSlider.module.scss';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import SliderPrevIcon from './Icons/SliderPrevIcon';
import SliderNextIcon from './Icons/SliderNextIcon';
import ReviewsSwiper from 'components/ReviewsSwiper/ReviewsSwiper';
import { usewpdataSelectors } from 'lib/wpdataState/selectors';
import DesktopNav from './DesktopNav';
import LazyLoad from 'react-lazy-load';
import glideImage from '../../../utils/glideImage';

type SliderType = {
  tablet?: boolean;
  mobile?: boolean;
  dotsInMobile?: boolean;
  isPagingImages?: boolean;
  arrowsInMobile?: boolean;
  arrayLength: number;
  version: 'version-1' | 'version-2';
};

const ImageSlider = ({ tablet = true, mobile = true, arrowsInMobile = false, version }: SliderType) => {
  const [mainSlider, setMainSlider] = useState<Slider | null>(null);
  const [thumbSlider, setThumbSlider] = useState<Slider | null>(null);
  const [height, setHeight] = useState(0);
  const swiperRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const removeAriaHidden = () => {
    const hiddenSlides = document.querySelectorAll('.slick-slide[aria-hidden="true"]');
    hiddenSlides.forEach((slide) => {
      slide.removeAttribute('aria-hidden');
    });
  };

  const settings = {
    accessibility: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    pauseOnHover: true,
    arrows: false,
    prevArrow: <SliderPrevIcon arrowsInMobile={arrowsInMobile} />,
    nextArrow: <SliderNextIcon arrowsInMobile={arrowsInMobile} />,
    asNavFor: thumbSlider || undefined,
    // 1) Remove aria-hidden on init
    onInit: removeAriaHidden,
    // 2) Remove aria-hidden before changing slides
    beforeChange: (_current: number, _next: number) => removeAriaHidden(),
    // 3) Also do it again after the slide changes
    afterChange: () => removeAriaHidden(),
  };

  const thumbnailSettings = {
    accessibility: false,
    slidesToShow: 5,
    slidesToScroll: 1,
    asNavFor: mainSlider || undefined,
    swipeToSlide: true,
    // We keep focusOnSelect true so the main slider updates on click
    focusOnSelect: true,
    centerMode: false,
    arrows: false,
    onInit: removeAriaHidden,
    beforeChange: () => removeAriaHidden(),
    afterChange: () => removeAriaHidden(),
    responsive: [
      {
        breakpoint: 768,
        settings: {
          arrows: false,
          centerMode: false,
          slidesToShow: 5,
        },
      },
    ],
  };

  useEffect(() => {
    setHeight(Number(swiperRef?.current?.clientHeight) + Number(wrapperRef?.current?.clientHeight));
  }, [swiperRef?.current?.clientHeight, wrapperRef?.current?.clientHeight]);

  const { hero, Settings } = usewpdataSelectors();

  return (
    <section
      style={
        {
          '--hero-section-height': `${height + 32}px`,
          '--hero-thumbnail-border': hero?.styles?.activeHeroThumbnailBorder,
        } as React.CSSProperties
      }
      className={`
        ${s.imageSlider}
        ${mobile ? '' : s['imageSlider--tablet']}
        ${tablet ? '' : s['imageSlider--desktop']}
        ${version === 'version-1' ? s['imageSlider--version-1'] : s['imageSlider--version-2']}
      `}
    >
      <div ref={swiperRef}>
        {hero.gallery && (
          <div style={{ position: 'relative' }}>
            <DesktopNav mainSlider={mainSlider} />
            <Slider
              {...settings}
              ref={(sliderInstance) => setMainSlider(sliderInstance)}
              className={s.imageSlider__container}
              lazyLoad="ondemand"
            >
              {hero.gallery.map((item, index) => {
                if (item.use_video) {
                  return (
                    <Video
                      key={index}
                      videoId={item.cloudflare_video_id}
                      brandName={Settings.brandName}
                      autoplay
                      loop
                      controls
                    />
                  );
                }
                return (
                  <Image
                    key={index}
                    desktopImage={glideImage(item.gallery_image, { w: 600, h: 600 })}
                    mobileImage={glideImage(item.gallery_image_mobile, { w: 800, h: 800 })}
                    brandName={Settings.brandName}
                  />
                );
              })}
            </Slider>
          </div>
        )}

        {hero.gallery && (
          <LazyLoad>
            <Slider
              {...thumbnailSettings}
              className={s.thumb__slider}
              ref={(slider) => setThumbSlider(slider)}
              lazyLoad="ondemand"
            >
              {hero.gallery.map((item, index) => {
                const thumbnailSrc = item.use_video
                  ? item.cloudflare_video_thumb
                  : item.gallery_image_thumbnail || item.gallery_image;

                return (
                  <img
                    key={index}
                    src={glideImage(thumbnailSrc, { w: 145, h: 145 })}
                    alt={Settings.brandName}
                    style={{ marginRight: '6px', width: '100%' }}
                    width={64}
                    height={64}
                  />
                );
              })}
            </Slider>
          </LazyLoad>
        )}
      </div>
      <div ref={wrapperRef}>
        <ReviewsSwiper />
      </div>
    </section>
  );
};

export default ImageSlider;
